import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { UsersThree, ArrowRight, Percent, Sparkle } from "@phosphor-icons/react";

/**
 * Recruiter-facing panel that surfaces other candidates in the workspace who
 * are semantically closest to the currently-viewed candidate.
 *
 * Backed by GET /api/candidates/:id/similar which returns semantic kNN
 * results (V2.0.76). The response still ships legacy Jaccard fields
 * (`overlap_score`, `shared_skills`) as an at-a-glance skills overlay,
 * plus `similarity_score` (cosine) and `mode: "semantic"` for the badge.
 *
 * Renders inline in CandidateDrawer. Clicking a card asks the parent to
 * switch the drawer to that candidate.
 */
export default function SimilarCandidates({ candidateId, onOpenCandidate, limit = 6 }) {
    const [items, setItems] = useState([]);
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(true);
    const [baseCount, setBaseCount] = useState(0);
    const [mode, setMode] = useState("");

    useEffect(() => {
        if (!candidateId) return;
        let mounted = true;
        // Clear stale results immediately so switching drawers doesn't flash
        // the previous candidate's similar list before the new fetch resolves.
        setItems([]);
        setReason("");
        setMode("");
        setLoading(true);
        api
            .get(`/candidates/${candidateId}/similar`, { params: { limit } })
            .then((r) => {
                if (!mounted) return;
                setItems(Array.isArray(r.data?.items) ? r.data.items : []);
                setBaseCount(r.data?.base_skills_count || 0);
                setReason(r.data?.reason || "");
                setMode(r.data?.mode || "");
            })
            .catch((e) => logError("Similar candidates load failed:", e))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [candidateId, limit]);

    if (loading) {
        return (
            <div data-testid="similar-candidates-loading" className="text-xs text-[#A3A3A3]">
                Finding similar candidates…
            </div>
        );
    }

    if (reason) {
        return (
            <div data-testid="similar-candidates-empty-reason" className="text-xs text-[#A3A3A3]">
                {reason}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div data-testid="similar-candidates-empty" className="text-xs text-[#A3A3A3]">
                No semantically similar candidates in the workspace yet.
            </div>
        );
    }

    return (
        <div data-testid="similar-candidates-panel" className="space-y-2">
            {mode === "semantic" && (
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-cobalt">
                    <Sparkle size={11} weight="fill" />
                    Semantic match · OpenAI embeddings
                </div>
            )}
            {items.map((c, idx) => {
                const semPct = Math.round((c.similarity_score || 0) * 100);
                const overlapPct = Math.round((c.overlap_score || 0) * 100);
                const primaryPct = c.similarity_score != null ? semPct : overlapPct;
                return (
                    <button
                        key={c.id}
                        type="button"
                        data-testid={`similar-candidate-${idx}`}
                        onClick={() => onOpenCandidate?.(c.id)}
                        className="w-full text-left border border-[#E5E5E5] hover:border-cobalt hover:bg-cobalt/[0.02] transition-colors px-3 py-2.5 flex items-start gap-3"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-sm text-ink truncate">{c.name || "—"}</p>
                                {c.code && (
                                    <span className="font-mono text-[10px] text-[#A3A3A3]">{c.code}</span>
                                )}
                                {c.stage && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#525252] bg-[#F5F5F5] px-1.5 py-0.5">
                                        {c.stage}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-[#525252] truncate mt-0.5">
                                {c.current_title || "—"}
                                {c.location ? ` · ${c.location}` : ""}
                                {typeof c.years_experience === "number"
                                    ? ` · ${c.years_experience}y`
                                    : ""}
                            </p>
                            {c.shared_skills?.length > 0 && (
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                    {c.shared_skills.slice(0, 5).map((s, i) => (
                                        <span
                                            key={`ss-${idx}-${i}`}
                                            className="text-[10px] bg-cobalt/10 text-cobalt font-bold px-1.5 py-0.5"
                                        >
                                            {s}
                                        </span>
                                    ))}
                                    {c.shared_skills.length > 5 && (
                                        <span className="text-[10px] text-[#A3A3A3] font-bold px-1.5 py-0.5">
                                            +{c.shared_skills.length - 5}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        <div
                            className="text-right shrink-0"
                            title={
                                c.similarity_score != null
                                    ? `Semantic cosine ${semPct}% · Skill overlap ${overlapPct}% (${c.overlap_count}/${baseCount})`
                                    : `Jaccard ${overlapPct}% — ${c.overlap_count}/${baseCount} skills match`
                            }
                        >
                            <p className="font-heading font-black text-lg leading-none text-cobalt">
                                {primaryPct}
                                <Percent size={10} weight="bold" className="inline -translate-y-1.5" />
                            </p>
                            <p className="text-[9px] font-bold uppercase tracking-wider text-[#A3A3A3] mt-0.5">
                                {c.similarity_score != null
                                    ? `sem · ${c.overlap_count}/${baseCount} skills`
                                    : `${c.overlap_count}/${baseCount} skills`}
                            </p>
                        </div>
                    </button>
                );
            })}
            {items.length > 0 && (
                <p className="text-[10px] text-[#A3A3A3] font-mono pt-1">
                    <UsersThree size={11} weight="bold" className="inline mr-1" />
                    {mode === "semantic"
                        ? `Ranked by semantic cosine · Base has ${baseCount} canonical skills`
                        : `Ranked by Jaccard skill overlap · Base has ${baseCount} canonical skills`}
                </p>
            )}
        </div>
    );
}
